/**
 * IngredientLineHint — how the editor read an ingredient line, and the picker
 * that corrects it (follow-up to #792).
 *
 * The pills are buttons: the quantity, the unit (or a dashed « + unité » /
 * « + quantité » when nothing was read). A pick rewrites the line's text
 * through `rewriteIngredientLine`, and the picker only offers units the parser
 * reads, so the hint always shows what was picked. « Autre… » writes the
 * cook's own word, which the parser may not know yet; the editor logs it.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Paprika, PaprikaSpacing, PaprikaTypography, PaprikaRadius } from '../../styles/theme';
import { readIngredientLine } from '../../utils/ingredientEditLines';
import {
  linePartsOf,
  pickerUnits,
  rewriteIngredientLine,
  type LineLanguage,
} from '../../utils/ingredientLineCorrection';
import { formatQuantity } from '../../utils/ingredientParser';

interface IngredientLineHintProps {
  text: string;
  lang: LineLanguage;
  pickerOpen: boolean;
  corrected: boolean;
  onTogglePicker: () => void;
  onRewrite: (text: string, otherUnit?: string) => void;
  onClosePicker: () => void;
}

const sameUnit = (read: string, chosen: string) => {
  const r = read.toLowerCase();
  const c = chosen.toLowerCase();
  return r === c || r === `${c}s` || r === `${c}es`;
};

/** « 2,5 » and "2.5" both; anything unreadable is null. */
const typedQuantity = (value: string): number | null => {
  const n = parseFloat(value.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const IngredientLineHint: React.FC<IngredientLineHintProps> = ({
  text,
  lang,
  pickerOpen,
  corrected,
  onTogglePicker,
  onRewrite,
  onClosePicker,
}) => {
  const { t } = useTranslation();
  const read = readIngredientLine(text);
  const parts = linePartsOf(text);

  const [quantityDraft, setQuantityDraft] = useState('');
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherDraft, setOtherDraft] = useState('');

  // The quantity field follows the line, so a pill or a step shows its number.
  useEffect(() => {
    setQuantityDraft(parts.quantity ? formatQuantity(parts.quantity, parts.unit) : '');
  }, [parts.quantity, parts.unit]);
  useEffect(() => {
    if (!pickerOpen) setOtherOpen(false);
  }, [pickerOpen]);

  if (!text.trim()) return null;

  const pill = (label: string, testID: string, ask = false) => (
    <TouchableOpacity
      style={[styles.pill, ask && styles.pillAsk, pickerOpen && styles.pillOpen]}
      onPress={onTogglePicker}
      activeOpacity={0.75}
      testID={testID}
      accessibilityRole="button"
    >
      <Text style={[styles.pillText, pickerOpen && styles.pillTextOpen]}>{label}</Text>
      <Feather name="chevron-down" size={11} color={pickerOpen ? '#fff' : Paprika.accent} />
    </TouchableOpacity>
  );

  const setQuantity = (quantity: number | null) => onRewrite(rewriteIngredientLine(text, { quantity }, lang));
  const step = (delta: number) => {
    const current = parts.quantity ?? 0;
    const next = Math.max(0, current < 1 && delta > 0 ? 1 : current + delta);
    setQuantity(next || null);
  };
  const pickUnit = (unit: string) => {
    onRewrite(rewriteIngredientLine(text, { unit, quantity: parts.quantity ?? 1 }, lang));
    onClosePicker();
  };
  const pickOther = () => {
    const word = otherDraft.trim();
    if (!word) return;
    onRewrite(rewriteIngredientLine(text, { unit: word, quantity: parts.quantity ?? 1 }, lang), word);
    setOtherDraft('');
    onClosePicker();
  };

  return (
    <View style={styles.wrap}>
      <View style={styles.hint} testID="edit-ingredient-hint">
        {read ? (
          <>
            <Text style={styles.hintText}>{t('recipeDetail.ingredientReadAs', 'Lu comme')}</Text>
            {pill(read.quantity, 'edit-ingredient-pill-quantity')}
            {read.unit
              ? pill(read.unit, 'edit-ingredient-pill-unit')
              : pill(t('recipeDetail.addUnit', '+ unité'), 'edit-ingredient-pill-unit', true)}
          </>
        ) : (
          pill(t('recipeDetail.addQuantity', '+ quantité'), 'edit-ingredient-pill-quantity', true)
        )}
        <Text style={styles.hintText} numberOfLines={1}>{read ? read.food : parts.food}</Text>
        {corrected && !pickerOpen ? (
          <View style={styles.corrected} testID="edit-ingredient-corrected">
            <Feather name="check" size={12} color={Paprika.success} />
            <Text style={styles.correctedText}>{t('recipeDetail.ingredientCorrected', 'Corrigé')}</Text>
          </View>
        ) : null}
      </View>

      {pickerOpen ? (
        <View style={styles.picker} testID="edit-ingredient-picker">
          <Text style={styles.pickerLabel}>{t('recipeDetail.unitLabel', 'UNITÉ')}</Text>
          <View style={styles.chips}>
            <TouchableOpacity
              style={[styles.chip, !parts.unit && styles.chipOn]}
              onPress={() => pickUnit('')}
              testID="edit-unit-none"
            >
              <Text style={[styles.chipText, !parts.unit && styles.chipTextOn]}>
                {t('recipeDetail.unitNone', 'aucune')}
              </Text>
            </TouchableOpacity>
            {pickerUnits(lang).map(unit => {
              const on = !!parts.unit && sameUnit(parts.unit, unit);
              return (
                <TouchableOpacity
                  key={unit}
                  style={[styles.chip, on && styles.chipOn]}
                  onPress={() => pickUnit(unit)}
                  testID={`edit-unit-chip-${unit}`}
                >
                  <Text style={[styles.chipText, on && styles.chipTextOn]}>{unit}</Text>
                </TouchableOpacity>
              );
            })}
            <TouchableOpacity style={styles.chip} onPress={() => setOtherOpen(o => !o)} testID="edit-unit-other">
              <Text style={styles.chipText}>{t('recipeDetail.unitOther', 'Autre…')}</Text>
            </TouchableOpacity>
          </View>
          {otherOpen ? (
            <View style={styles.otherRow}>
              <TextInput
                style={[styles.smallInput, styles.otherInput]}
                value={otherDraft}
                onChangeText={setOtherDraft}
                onSubmitEditing={pickOther}
                placeholder={t('recipeDetail.unitOtherPlaceholder', 'ex. filet')}
                placeholderTextColor={Paprika.textMuted}
                autoFocus
                testID="edit-unit-other-input"
              />
              <TouchableOpacity style={styles.okButton} onPress={pickOther} testID="edit-unit-other-ok">
                <Text style={styles.okText}>{t('common.ok', 'OK')}</Text>
              </TouchableOpacity>
            </View>
          ) : null}

          <Text style={styles.pickerLabel}>{t('recipeDetail.quantityLabel', 'QUANTITÉ')}</Text>
          <View style={styles.quantityRow}>
            <TouchableOpacity
              style={styles.stepButton}
              onPress={() => step(-1)}
              testID="edit-qty-minus"
              accessibilityLabel={t('recipeDetail.quantityLess', 'Moins')}
            >
              <Feather name="minus" size={16} color={Paprika.accent} />
            </TouchableOpacity>
            <TextInput
              style={[styles.smallInput, styles.quantityInput]}
              value={quantityDraft}
              onChangeText={setQuantityDraft}
              onEndEditing={() => setQuantity(typedQuantity(quantityDraft))}
              onSubmitEditing={() => setQuantity(typedQuantity(quantityDraft))}
              keyboardType="decimal-pad"
              placeholder="0"
              placeholderTextColor={Paprika.textMuted}
              testID="edit-qty-input"
            />
            <TouchableOpacity
              style={styles.stepButton}
              onPress={() => step(1)}
              testID="edit-qty-plus"
              accessibilityLabel={t('recipeDetail.quantityMore', 'Plus')}
            >
              <Feather name="plus" size={16} color={Paprika.accent} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.chip} onPress={() => setQuantity(0.5)} testID="edit-qty-half">
              <Text style={styles.chipText}>½</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.chip} onPress={() => setQuantity(0.25)} testID="edit-qty-quarter">
              <Text style={styles.chipText}>¼</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: {
    gap: PaprikaSpacing.sm,
    paddingRight: 46,
  },
  hint: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    paddingLeft: PaprikaSpacing.xs,
  },
  hintText: {
    ...PaprikaTypography.caption,
    color: Paprika.textMuted,
    flexShrink: 1,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.sm,
    borderWidth: 1,
    borderColor: 'transparent',
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  pillAsk: {
    backgroundColor: Paprika.cardBg,
    borderColor: Paprika.accent,
    borderStyle: 'dashed',
  },
  pillOpen: {
    backgroundColor: Paprika.accent,
    borderColor: Paprika.accent,
  },
  pillText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    color: Paprika.accent,
    fontVariant: ['tabular-nums'],
  },
  pillTextOpen: {
    color: '#fff',
  },
  corrected: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  correctedText: {
    ...PaprikaTypography.caption,
    color: Paprika.success,
  },
  picker: {
    backgroundColor: Paprika.cardBg,
    borderRadius: PaprikaRadius.lg,
    padding: PaprikaSpacing.md,
    gap: PaprikaSpacing.sm,
    shadowColor: 'rgb(82, 68, 60)',
    shadowOpacity: 0.12,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
  },
  pickerLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    letterSpacing: 0.6,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.full,
    paddingHorizontal: 11,
    paddingVertical: 6,
  },
  chipOn: {
    backgroundColor: Paprika.accent,
  },
  chipText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    color: Paprika.textSecondary,
  },
  chipTextOn: {
    color: '#fff',
  },
  otherRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  smallInput: {
    borderWidth: 1,
    borderColor: Paprika.border,
    borderRadius: PaprikaRadius.md,
    backgroundColor: Paprika.cardBg,
    paddingVertical: 8,
    paddingHorizontal: PaprikaSpacing.md,
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
  },
  otherInput: {
    flex: 1,
  },
  okButton: {
    backgroundColor: Paprika.accent,
    borderRadius: PaprikaRadius.full,
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: 8,
  },
  okText: {
    ...PaprikaTypography.labelMedium,
    color: '#fff',
  },
  quantityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  stepButton: {
    width: 34,
    height: 34,
    borderRadius: PaprikaRadius.full,
    backgroundColor: Paprika.surfaceLow,
    alignItems: 'center',
    justifyContent: 'center',
  },
  quantityInput: {
    width: 72,
    textAlign: 'center',
    fontVariant: ['tabular-nums'],
  },
});

export default IngredientLineHint;
